import { PsHttpService } from '@patsnap/tahiti-core';
import type { PatSnapCommonResponseData } from '@patsnap/tahiti-core/common/ps-http';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { FactoryPsHttpSSE, type PsHttpSSE } from '@patsnap-utils/sse';
import type { ISSEResponse } from '../types/copilot-event';

// ============ API 相关类型定义 ============

/**
 * 创建聊天会话的响应
 */
export interface IChatCreateResponse {
    chat_id: string;
    new_chat: boolean;
}

/**
 * 开始对话（completions）的请求参数
 */
export interface IChatCompletionsRequest {
    chat_id: string;
    /** 用户输入的消息/规则 */
    message: string;
}

/**
 * 开始对话（completions）的响应
 */
export interface IChatCompletionsResponse {
    /** 请求是否成功 */
    status: boolean;
    /** 错误码，0 表示成功 */
    error_code: number;
}
// ============ API 接口定义 ============

export interface ChatApi {
    /**
     * 创建聊天会话
     * POST /work/project/chat/create
     * @param projectId 项目ID
     * @param newChat 是否创建新聊天（可选）- 只有在点击对话框中的 "new" 时才传 true
     * @returns 聊天会话信息，包含 chat_id 和 new_chat 标识
     */
    createChat(projectId: string, newChat?: boolean): Promise<IChatCreateResponse>;

    /**
     * 开始新对话（仅当 new_chat=true 时调用）
     * POST /work/project/chat/completions
     * @param request 请求参数
     * @returns completions 响应
     */
    startCompletions(request: IChatCompletionsRequest): Promise<IChatCompletionsResponse>;

    /**
     * 获取消息流（SSE）- 推荐使用的新方法
     * 返回 PsHttpSSE 实例，支持链式调用配置
     * POST /work/project/chat/message
     * @param chatId 聊天ID
     * @param lastId 最后一条消息ID（可选），用于断线重连
     * @returns PsHttpSSE 实例，需手动调用 .onMessage().onError().start()
     */
    getMessageStream(chatId: string, lastId?: string): PsHttpSSE<ISSEResponse>;

    /**
     * 确认开始处理
     * POST /work/project/chat/batch-extract
     */
    batchExtract(chatId: string): Promise<{ success: boolean }>;
}

// ============ API 实现 ============

@injectable()
export class ChatApiImpl extends PsHttpService implements ChatApi {
    private baseURL = `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/work/project`;

    @inject(FactoryPsHttpSSE)
    private _factorySsePsHttp!: FactoryPsHttpSSE;

    get options() {
        return {
            baseURL: this.baseURL,
            headers: {
                'X-API-Version': '1.0',
            },
        };
    }

    /**
     * 创建聊天会话
     * POST /work/project/chat/create
     */
    createChat(projectId: string, newChat?: boolean): Promise<IChatCreateResponse> {
        const requestData: { work_project_id: string; new_chat?: boolean } = {
            work_project_id: projectId,
        };

        // 只有在传递了 newChat 参数时才添加到请求中
        if (newChat !== undefined) {
            requestData.new_chat = newChat;
        }

        return this.post<PatSnapCommonResponseData<IChatCreateResponse>>({
            url: '/chat/create',
            data: requestData,
            headers: {
                'X-API-Version': '1.0',
                'X-PatSnap-From': 's-analytics-telecom',
            },
        }).then((res) => {
            return res.data as IChatCreateResponse;
        });
    }

    /**
     * 开始新对话
     * POST /work/project/chat/completions
     */
    startCompletions(request: IChatCompletionsRequest): Promise<IChatCompletionsResponse> {
        return this.post<PatSnapCommonResponseData<IChatCompletionsResponse>>({
            url: '/chat/completions',
            data: request,
            headers: {
                'X-API-Version': '1.0',
                'X-PatSnap-From': 's-analytics-telecom',
            },
        }).then((res) => {
            // 检查响应格式
            // 情况1：PsHttpService 包装后的格式 { data: { status, error_code } }
            const resAny = res as any;
            if (resAny && resAny.data && typeof resAny.data === 'object' && 'status' in resAny.data) {
                return {
                    status: resAny.data.status,
                    error_code: resAny.data.error_code,
                };
            }

            // 情况2：后端直接返回 { status, error_code }
            if (res && typeof res === 'object' && 'status' in res && 'error_code' in res) {
                return res as unknown as IChatCompletionsResponse;
            }

            // 情况3：其他格式，返回失败
            console.warn('[ChatApi] Unknown response format, returning error');
            return {
                status: false,
                error_code: -1,
            } as IChatCompletionsResponse;
        }).catch((err) => {
            console.error('[ChatApi] startCompletions error:', err);
            // 返回失败响应而不是抛出错误
            return {
                status: false,
                error_code: -1,
            } as IChatCompletionsResponse;
        });
    }

    /**
     * 获取消息流（SSE）- 推荐使用的新方法
     * 返回 PsHttpSSE 实例，由调用方配置事件处理器并启动
     * POST /work/project/chat/message
     */
    getMessageStream(chatId: string, lastId?: string): PsHttpSSE<ISSEResponse> {
        // 构建请求数据
        const requestData: Record<string, string> = { chat_id: chatId };

        // 如果提供了 lastId，添加到请求体中
        if (lastId) {
            requestData.last_id = lastId;
        }
        // 创建并返回 SSE 客户端实例（未启动）
        // 泛型参数 ISSEResponse 表示 response 的类型
        // 参考 results 包的实现：调用 .init() 并通过 .setFetchHeaders() 设置请求头
        return this._factorySsePsHttp<ISSEResponse>()
            .init({
                url: `${this.baseURL}/chat/message`,
                data: requestData,
                hasFinalMessage: () => true,
            })
            .setFetchHeaders({
                'X-API-Version': '1.0',
                'X-PatSnap-From': 's-analytics-telecom',
            });
    }

    /**
     * 确认开始处理
     * POST /work/project/chat/batch-extract
     */
    batchExtract(chatId: string): Promise<{ success: boolean }> {
        return this.post<PatSnapCommonResponseData<{ success: boolean }>>({
            url: '/chat/batch-extract',
            data: { chat_id: chatId },
            headers: {
                'X-API-Version': '1.0',
                'X-PatSnap-From': 's-analytics-telecom',
            },
        }).then((res) => {
            return res.data as { success: boolean };
        });
    }
}

export const ChatApi = Symbol('ChatApi');
